// Janela de Proficiência de Armas (como a do cliente do Tibia 15): lista das armas, insígnia de maestria, barra de
// experiência e a árvore de bônus por nível (um bônus por nível, escolhido quando o nível é liberado). Ícones oficiais.
// Visual "Mestre de Armas": aço escuro, dourado de maestria e nós da árvore com estado claro. CSS em
// client/css-parts/wheel.css (prefixo .pf-; o .prof- antigo continua em styles.css).
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { perkIcon, perkText, profMastery, profMaxXp, profNext, profTable, profUnlocked, type ProfPerk, type ProfState, type ProfTree } from '../../../../shared/src/game/proficiency';
import { catalogNow } from '../items';
import { spellsNow } from '../spells';
import { SpellIcon } from './SpellIcon';

const IMG = '/assets/interface/images/proficiency/';
let treesP: Promise<Record<string, ProfTree>> | null = null;
const loadTrees = () => (treesP ??= fetch('/data/proficiencies.json').then((r) => r.json()).then((d: { profs: Record<string, ProfTree> }) => d.profs));
const n = (v: number) => v.toLocaleString('pt-BR');
const short = (v: number) => v >= 1e6 ? `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : v >= 1e4 ? `${Math.round(v / 1e3)} mil` : n(v);

function PerkIcon(props: { p: ProfPerk; size?: number }) {
  const ic = perkIcon(props.p), size = props.size ?? 32;
  if ('spell' in ic) {
    const sp = spellsNow().find((s) => s.name === ic.spell);
    return sp ? <SpellIcon spell={sp} size={size} /> : <span class="pf-ico" style={{ width: size, height: size }} />;
  }
  return <span class="pf-ico" style={{ width: size, height: size, backgroundImage: `url(${ic.sheet})`, backgroundPosition: `-${ic.index * size}px 0`, backgroundSize: `auto ${size}px` }} />;
}

/** progresso (0..100) dentro do nível atual até a próxima meta */
function stepPct(tree: ProfTree, table: number[], xp: number) {
  const next = profNext(tree, table, xp);
  if (!next) return 100;
  const prev = [0, ...table].filter((x) => x <= xp).pop() ?? 0;
  return Math.min(100, ((xp - prev) / Math.max(1, next - prev)) * 100);
}

export function Proficiency(props: { items: ItemInstance[]; state: Record<string, ProfState>; onSelect: (weaponId: number, level: number, index: number) => void;
  Img: (p: { typeId: number; count: number }) => preact.JSX.Element | null; preferred?: number }) {
  const [trees, setTrees] = useState<Record<string, ProfTree> | null>(null);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [sel, setSel] = useState<number | null>(props.preferred ?? null);
  const [hover, setHover] = useState<{ lv: number; i: number } | null>(null);
  // passo a passo abre sozinho enquanto nenhuma arma tem experiência
  const [help, setHelp] = useState(() => !Object.values(props.state).some((s) => s.xp > 0));
  useEffect(() => { void loadTrees().then(setTrees); }, []);
  const cat = catalogNow();
  const equipped = props.items.find((i) => i.place === 'equip' && i.slot === 'weapon')?.typeId;
  // armas: as que o personagem tem, as que já têm experiência e (opcional) todas do jogo
  const list = useMemo(() => {
    if (!cat) return [] as number[];
    const ids = new Set<number>();
    for (const i of props.items) if (cat.get(i.typeId)?.prof) ids.add(i.typeId);
    for (const k of Object.keys(props.state)) ids.add(Number(k));
    if (all) for (const [id, t] of cat) if (t.prof && t.slot === 'weapon') ids.add(id);
    return [...ids].filter((id) => cat.get(id)?.prof && (!q || cat.get(id)!.name.toLowerCase().includes(q.toLowerCase())))
      .sort((a, b) => (b === equipped ? 1 : 0) - (a === equipped ? 1 : 0) || (props.state[String(b)]?.xp ?? 0) - (props.state[String(a)]?.xp ?? 0) || cat.get(a)!.name.localeCompare(cat.get(b)!.name));
  }, [cat, props.items, props.state, q, all, equipped]);
  // resumo do mestre de armas (todas as armas com experiência, independente da busca)
  const summary = useMemo(() => {
    let weapons = 0, levels = 0, pending = 0, gold = 0;
    if (!cat || !trees) return { weapons, levels, pending, gold };
    for (const [k, s] of Object.entries(props.state)) {
      const it = cat.get(Number(k)); if (!it?.prof || !(s.xp > 0)) continue;
      const tr = trees[String(it.prof)]; if (!tr) continue;
      const tb = profTable(it), u = profUnlocked(tr, tb, s.xp);
      weapons++; levels += u;
      pending += Math.max(0, u - s.perks.slice(0, u).filter((x) => x >= 0).length);
      if (profMastery(tr, tb, s.xp).tier === 'gold') gold++;
    }
    return { weapons, levels, pending, gold };
  }, [cat, trees, props.state]);
  const cur = sel ?? equipped ?? list[0] ?? null;
  const t = cur ? cat?.get(cur) : undefined;
  const tree = t?.prof && trees ? trees[String(t.prof)] : undefined;
  if (!trees || !cat) return <div class="pf"><p class="pf-dim">Carregando...</p></div>;
  const st = cur ? props.state[String(cur)] ?? { xp: 0, perks: [] } : { xp: 0, perks: [] };
  const table = t ? profTable(t) : [];
  const unlocked = tree ? profUnlocked(tree, table, st.xp) : 0;
  const mast = tree ? profMastery(tree, table, st.xp) : { level: 0, tier: '' as const };
  const next = tree ? profNext(tree, table, st.xp) : null;
  const max = tree ? profMaxXp(tree, table) : 0;
  const pctBar = tree ? stepPct(tree, table, st.xp) : 0;
  const badge = `${IMG}icon-masterylevel-${mast.level}${mast.tier ? '-' + mast.tier : ''}.png`;
  const desc = hover && tree ? tree.levels[hover.lv]?.[hover.i] : undefined;
  const chosenCount = tree ? st.perks.slice(0, unlocked).filter((x) => x >= 0).length : 0;
  return (
    <div class="pf">
      <div class="pf-head">
        <div class="pf-crest"><span>⚔</span></div>
        <div class="pf-head-txt">
          <div class="pf-title">Mestre de Armas
            <button class="pf-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="pf-sub">Cada arma ganha experiência nos seus abates e libera uma árvore de bônus própria.</div>
        </div>
        <div class="pf-stats">
          <div><b>{summary.weapons}</b><small>armas treinadas</small></div>
          <div><b>{summary.levels}</b><small>níveis liberados</small></div>
          <div class={summary.pending ? 'pf-hot' : ''}><b>{summary.pending}</b><small>bônus a escolher</small></div>
          <div class="pf-gold"><b>{summary.gold}</b><small>maestria de ouro</small></div>
        </div>
      </div>

      {help && (
        <div class="pf-steps">
          <div><i>🗡️</i><b>1. Equipe e cace</b><small>A arma equipada ganha experiência a cada abate: mais estrelas no bestiário dão mais (bosses dão muito).</small></div>
          <div><i>🌳</i><b>2. Escolha os bônus</b><small>Cada nível liberado deixa você escolher 1 bônus. Clique no ícone; clique de novo para tirar.</small></div>
          <div><i>🏅</i><b>3. Chegue à maestria</b><small>Depois do último nível vêm prata e ouro. Os bônus valem quando a arma estiver equipada.</small></div>
        </div>
      )}

      <div class="pf-body">
        <div class="pf-left">
          <input class="pf-search" placeholder="🔎 Buscar arma" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          <label class="pf-all"><input type="checkbox" checked={all} onChange={(e) => setAll((e.target as HTMLInputElement).checked)} /> Todas as armas do jogo</label>
          <div class="pf-list">
            {list.map((id) => {
              const it = cat.get(id)!, s = props.state[String(id)];
              const tr = trees[String(it.prof)], tb = profTable(it);
              const m = tr ? profMastery(tr, tb, s?.xp ?? 0) : { level: 0, tier: '' };
              const pend = tr && s ? profUnlocked(tr, tb, s.xp) > s.perks.filter((x) => x >= 0).length : false;
              const bar = tr ? stepPct(tr, tb, s?.xp ?? 0) : 0;
              return (
                <button key={id} class={'pf-row' + (id === cur ? ' on' : '') + (pend ? ' pend' : '') + (m.tier ? ' ' + m.tier : '')} onClick={() => setSel(id)} title={it.name}>
                  <span class="item-slot"><props.Img typeId={id} count={1} /></span>
                  <span class="pf-row-txt">
                    <b>{it.name}</b>
                    <small>{id === equipped && <em class="pf-eq">equipada</em>}nível {m.level}{m.tier === 'gold' ? ' ★★' : m.tier === 'silver' ? ' ★' : ''}</small>
                    <span class="pf-mini"><i style={{ width: `${bar}%` }} /></span>
                  </span>
                  {pend && <span class="pf-badge-new" title="Bônus para escolher">!</span>}
                </button>
              );
            })}
            {!list.length && <div class="pf-empty"><span>🗡️</span><small>Nenhuma arma com proficiência. Marque "Todas as armas do jogo" para ver as árvores.</small></div>}
          </div>
        </div>
        {t && tree ? (
          <div class="pf-right">
            <div class={'pf-weapon' + (mast.tier ? ' ' + mast.tier : '')}>
              <span class="pf-wslot"><span class="item-slot big"><props.Img typeId={cur!} count={1} /></span></span>
              <div class="pf-wtxt">
                <b>{t.name}</b>
                <small>{tree.name}{tree.wiki ? ' (TibiaWiki)' : ''}</small>
                <small class={cur === equipped ? 'pf-ok' : 'pf-dim'}>{cur === equipped ? '✔ Equipada: os bônus valem agora' : 'Os bônus valem quando ela estiver equipada'}</small>
                <div class="pf-bar"><i style={{ width: `${pctBar}%` }} /><span>{next ? `${n(st.xp)} / ${n(next)}` : `${n(max)} — maestria completa`}</span></div>
                <div class="pf-wnums">
                  <span>Nível <b>{mast.level}</b>/{tree.levels.length}</span>
                  <span>Bônus <b>{chosenCount}</b>/{unlocked}</span>
                  <span>{next ? <>Próx. meta <b>{short(next - st.xp)}</b> xp</> : <b>Máximo</b>}</span>
                </div>
              </div>
              <img class="pf-mbadge" src={badge} alt={`Maestria ${mast.level}`} title={`Nível ${mast.level} de ${tree.levels.length}${mast.tier ? mast.tier === 'gold' ? ' · maestria de ouro' : ' · maestria de prata' : ''}`} />
            </div>
            <div class="pf-legend"><span><i class="l-chosen" />escolhido</span><span><i class="l-pick" />disponível</span><span><i class="l-lock" />bloqueado</span></div>
            <div class="pf-tree">
              {tree.levels.map((perks, lv) => {
                const open = lv < unlocked, chosen = st.perks[lv] ?? -1, need = table[lv] ?? 0;
                const state = !open ? 'lock' : chosen < 0 ? 'pick' : 'done';
                return (
                  <div key={lv} class={'pf-level ' + state}>
                    <span class="pf-lv"><b>{state === 'lock' ? '🔒' : lv + 1}</b><small>{short(need)}</small></span>
                    <div class="pf-perks">
                      {perks.map((p, i) => (
                        <button key={i} class={'pf-perk' + (chosen === i ? ' chosen' : '') + (!open ? ' locked' : '')} disabled={!open}
                          onMouseEnter={() => setHover({ lv, i })} onMouseLeave={() => setHover(null)} onFocus={() => setHover({ lv, i })}
                          onClick={() => props.onSelect(cur!, lv, chosen === i ? -1 : i)} title={perkText(p)}>
                          <PerkIcon p={p} />
                        </button>
                      ))}
                    </div>
                    <small class="pf-chosen">{chosen >= 0 ? perkText(perks[chosen]!) : open ? '✦ Escolha um bônus' : `Libera com ${n(need)} de experiência`}</small>
                  </div>
                );
              })}
            </div>
            <div class="pf-desc">{desc ? <><PerkIcon p={desc} size={32} /><span>{perkText(desc)}</span></> : <span class="pf-dim">Passe o mouse num bônus para ver o que ele faz. Clique para escolher (clique de novo para tirar). Trocar um bônus já escolhido só na cidade.</span>}</div>
            <small class="pf-dim">A experiência vem dos abates com a arma equipada: mais estrelas no bestiário dão mais (1 a 240 por criatura; bosses: 500, 5.000 ou 15.000). Depois do último nível há 2 níveis de maestria (prata e ouro).</small>
          </div>
        ) : <div class="pf-right"><div class="pf-empty"><span>⚔</span><small>Escolha uma arma.</small></div></div>}
      </div>
    </div>
  );
}
